/**
 * TMS 타일 합성기 — 타일 색인(`tmsTileIndex`)이 쓰는 fetch·마스크·합성의 실행 주체. 구현은 둘이고 결과는 같다:
 * Worker 합성기(기본)와 메인 스레드 합성기(Worker·OffscreenCanvas 가 없거나 Worker 가 `unsupported` 를 보낼 때의
 * 폴백). 선택은 `createTmsTileComposer` 가 기능 감지로 한다. spec inspection3d-tms-imagery 「타일 합성과 점유 마스크
 * 산출은 UI 스레드 밖에서 한다」
 */
import type { TileRange } from "./webMercatorTiles";
import type { TileOccupancy, TileXY } from "./tmsTileSource";
import {
  createTileSourceHandler,
  type ComposeResult,
  type TileSourceConfig,
  type TileSourceRequest,
  type TileSourceResponse,
  type TileWorkerInbound,
  type TileWorkerOutbound,
} from "./tmsTileWorkerProtocol";

export type { TileImage } from "./tmsTileWorkerProtocol";

export interface TmsTileComposer {
  probe(level: number, x: number, y: number): Promise<TileOccupancy | null>;
  compose(
    sourceLevel: number,
    range: TileRange,
    candidates: TileXY[]
  ): Promise<ComposeResult>;
  /** 진행 중인 작업의 결과는 버리고 스레드 자원을 놓는다 */
  dispose(): void;
}

const EMPTY_COMPOSE: ComposeResult = { image: null, occupancy: [] };

export function createMainThreadTileComposer(
  config: TileSourceConfig
): TmsTileComposer {
  let stopped = false;
  const handler = createTileSourceHandler(config, {
    fetch: (url, init) => fetch(url, init),
    decode: (blob) => createImageBitmap(blob),
    createCanvas: (width, height) => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      return canvas;
    },
    exportImage: (canvas) => canvas,
    isStopped: () => stopped,
  });
  return {
    probe: (level, x, y) =>
      handler && !stopped ? handler.probe(level, x, y) : Promise.resolve(null),
    compose: (sourceLevel, range, candidates) =>
      handler && !stopped
        ? handler.compose(sourceLevel, range, candidates)
        : Promise.resolve(EMPTY_COMPOSE),
    dispose: () => {
      stopped = true;
    },
  };
}

/** `Worker` 가운데 합성기가 쓰는 면 — 테스트는 fake 로 대신한다 */
export interface TileWorkerLike {
  postMessage(message: TileWorkerInbound, transfer?: Transferable[]): void;
  terminate(): void;
  onmessage: ((event: MessageEvent<TileWorkerOutbound>) => void) | null;
  onerror: ((event: ErrorEvent) => void) | null;
}

interface PendingRequest {
  request: TileSourceRequest;
  resolve: (response: TileSourceResponse) => void;
}

function runOnComposer(
  request: TileSourceRequest,
  composer: TmsTileComposer
): Promise<TileSourceResponse> {
  if (request.kind === "probe")
    return composer
      .probe(request.level, request.x, request.y)
      .then((occupancy) => ({ id: request.id, kind: "probe", occupancy }));
  return composer
    .compose(request.sourceLevel, request.range, request.candidates)
    .then((result) => ({ id: request.id, kind: "compose", ...result }));
}

function emptyResponse(request: TileSourceRequest): TileSourceResponse {
  return request.kind === "probe"
    ? { id: request.id, kind: "probe", occupancy: null }
    : { id: request.id, kind: "compose", ...EMPTY_COMPOSE };
}

/**
 * Worker 에 요청을 보내고 id 로 응답을 짝지운다. Worker 가 `unsupported` 를 보내거나 오류로 죽으면 폴백 합성기를 만들어
 * 아직 응답받지 못한 요청부터 다시 수행한다 — 호출자는 어느 쪽이 처리했는지 모른다.
 */
export function createWorkerTileComposer(
  config: TileSourceConfig,
  spawnWorker: () => TileWorkerLike,
  createFallback: () => TmsTileComposer
): TmsTileComposer {
  const worker = spawnWorker();
  const pending = new Map<number, PendingRequest>();
  let fallback: TmsTileComposer | null = null;
  let disposed = false;
  let nextId = 0;

  const switchToFallback = () => {
    if (fallback || disposed) return;
    const composer = createFallback();
    fallback = composer;
    worker.terminate();
    for (const [id, { request, resolve }] of pending) {
      pending.delete(id);
      runOnComposer(request, composer).then(resolve);
    }
  };

  worker.onmessage = (event) => {
    const message = event.data;
    if (message.kind === "unsupported") {
      switchToFallback();
      return;
    }
    const entry = pending.get(message.id);
    if (!entry) return;
    pending.delete(message.id);
    entry.resolve(message);
  };
  worker.onerror = () => switchToFallback();
  worker.postMessage({ kind: "init", config });

  const send = (request: TileSourceRequest): Promise<TileSourceResponse> => {
    if (disposed) return Promise.resolve(emptyResponse(request));
    if (fallback) return runOnComposer(request, fallback);
    return new Promise((resolve) => {
      pending.set(request.id, { request, resolve });
      worker.postMessage(request);
    });
  };

  return {
    probe: (level, x, y) =>
      send({ id: nextId++, kind: "probe", level, x, y }).then((response) =>
        response.kind === "probe" ? response.occupancy : null
      ),
    compose: (sourceLevel, range, candidates) =>
      send({ id: nextId++, kind: "compose", sourceLevel, range, candidates }).then(
        (response) =>
          response.kind === "compose"
            ? { image: response.image, occupancy: response.occupancy }
            : EMPTY_COMPOSE
      ),
    dispose: () => {
      disposed = true;
      worker.terminate();
      fallback?.dispose();
      for (const [id, { request, resolve }] of pending) {
        pending.delete(id);
        resolve(emptyResponse(request));
      }
    },
  };
}

export function canComposeInWorker(): boolean {
  return typeof Worker === "function" && typeof OffscreenCanvas === "function";
}

export function createTmsTileComposer(config: TileSourceConfig): TmsTileComposer {
  if (!canComposeInWorker()) return createMainThreadTileComposer(config);
  return createWorkerTileComposer(
    config,
    () => new Worker(new URL("./tmsTile.worker.ts", import.meta.url)),
    () => createMainThreadTileComposer(config)
  );
}
